import { api } from "../app/api";
import { TText } from "../pages/sales/sales.slice";

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
}

export interface IScheme {
  id?: number;
  name: string;
  author?: User;
  modifier?: User;
  building?: number;
}

interface IQueryResponse {
  count: number;
  next: any;
  previous: any;
  results: IScheme[];
}

interface IQueryParams {
  entity?: number;
  hall?: number;
  name?: string;
  exclude_state?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export type RectangleProperties = {
  rect: {
    x: number;
    y: number;
  };
  styles: {
    fill?: string;
    x?: string;
    y?: string;
    width?: string;
    height?: string;
    rx?: string;
    ry?: string;
  };
};

export type TextProperties = {
  styles: {
    fontSize: number;
    fill: string;
  };
  translations: {
    ro: string;
    ru: string;
    en: string;
  };
};

export type FanZoneDataProperties = {
  rectangle: RectangleProperties;
  text: TextProperties;
};

export type FanZoneData = {
  name: string;
  max_seats: number;
  fixed_seats: boolean;
  properties?: FanZoneDataProperties;
  free_seats: number,
  zone?: number,
  id?: number,
};

export interface ISchemeRead {
  id: number;
  name: string;
  hall_parts_data?: FanZoneData[];
  hall_parts: {
    name: string;
    rows: [];
    scheme: number;
    id: number;
  }[];
  hall: number;
  data: {
    [key: string]: {
      place: number;
      rect: {
        x: string;
        y: string;
      };
    };
  };
  properties: { text: TText, global: {styles: {}} };
  state: string;
}

export const schemeApi = api.injectEndpoints({
  endpoints: (build) => ({
    getSchemes: build.query<IQueryResponse, IQueryParams>({
      query: (params) => ({
        url: `/hall/admin/schemes/`,
        params: {
          entity: params.entity,
          hall: params.hall,
          exclude_state: params.exclude_state,
          name: params.name,
        },
      }),
      providesTags: (result, error, tag) => [{ type: "Scheme", tag }],
    }),
    readScheme: build.query<ISchemeRead, any>({
      query: (params) => ({
        url: `/hall/admin/schemes/${params.id}/`,
      }),
      keepUnusedDataFor: 0.0001,
      providesTags: (result, error, tag) => [{ type: "Scheme", tag }],
    }),
    createScheme: build.mutation<any, any>({
      query: (payload) => ({
        url: "/hall/admin/schemes/",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Scheme" }],
    }),
    updateScheme: build.mutation<any, any>({
      query: (params) => ({
        url: `/hall/admin/schemes/${params.id}/`,
        method: "PATCH",
        body: params.data,
      }),
      invalidatesTags: [{ type: "Scheme" }],
    }),
    applyScheme: build.mutation<any, any & { id: number }>({
      query: (payload) => ({
        url: `/hall/admin/schemes/${payload.id}/apply/`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Scheme" }],
    }),
    removeScheme: build.mutation<any, any>({
      query: (payload) => ({
        url: `/hall/admin/schemes/${payload.id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Scheme" }],
    }),
  }),
});

export const {
  useCreateSchemeMutation,
  useUpdateSchemeMutation,
  useApplySchemeMutation,
  useRemoveSchemeMutation,
  useReadSchemeQuery,
  useGetSchemesQuery,
  useLazyReadSchemeQuery,
} = schemeApi;
